import React, { useState, useRef, useEffect } from 'react';
import { Lock, Mail, Eye, EyeOff, Loader2, ShieldCheck, Check, Smartphone, ArrowLeft } from 'lucide-react';
import { backendUrl, setClientSession } from '../../components/client-portal/clientPortalApi';

const OTP_LEN = 6;

export default function ClientPortalLogin({ onLoggedIn }) {
  const [step, setStep] = useState('password'); // 'password' | 'otp'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // OTP state
  const [challengeId, setChallengeId] = useState('');
  const [phoneHint, setPhoneHint] = useState('');
  const [otp, setOtp] = useState(Array(OTP_LEN).fill(''));
  const [resendIn, setResendIn] = useState(0);
  const [info, setInfo] = useState('');
  const otpRefs = useRef([]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/client/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Login failed');
      // Password OK → SMS challenge issued.
      setChallengeId(data.challenge_id);
      setPhoneHint(data.phone_hint || '');
      setOtp(Array(OTP_LEN).fill(''));
      setResendIn(30);
      setStep('otp');
      setInfo(data.delivered === false
        ? 'We could not send an SMS (dev mode). Use the code below.'
        : `We sent a 6-digit code to ${data.phone_hint || 'your mobile'}.`);
      if (data.dev_code) setInfo(`Dev mode — your code is ${data.dev_code}`);
      setTimeout(() => otpRefs.current[0]?.focus(), 50);
    } catch (err) {
      setError(err.message || 'Unable to sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (codeStr) => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/client/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_id: challengeId, code: codeStr }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Verification failed');
      setClientSession(data.token, data.client);
      onLoggedIn?.(data);
    } catch (err) {
      setError(err.message || 'Verification failed.');
      setOtp(Array(OTP_LEN).fill(''));
      setTimeout(() => otpRefs.current[0]?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (resendIn > 0) return;
    setError('');
    setInfo('');
    try {
      const res = await fetch(`${backendUrl}/api/client/auth/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_id: challengeId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Could not resend');
      setResendIn(30);
      setOtp(Array(OTP_LEN).fill(''));
      setInfo(data.dev_code ? `Dev mode — your new code is ${data.dev_code}` : `New code sent to ${data.phone_hint || 'your mobile'}.`);
      setTimeout(() => otpRefs.current[0]?.focus(), 50);
    } catch (err) {
      setError(err.message || 'Could not resend the code.');
    }
  };

  const onOtpChange = (idx, val) => {
    const digits = val.replace(/\D/g, '');
    if (!digits && val !== '') return;
    const next = [...otp];
    if (digits.length > 1) {
      // paste
      const chars = digits.slice(0, OTP_LEN).split('');
      for (let i = 0; i < OTP_LEN; i++) next[i] = chars[i] || '';
      setOtp(next);
      const filled = Math.min(chars.length, OTP_LEN) - 1;
      otpRefs.current[filled]?.focus();
      if (chars.length >= OTP_LEN) submitOtp(next.join(''));
      return;
    }
    next[idx] = digits;
    setOtp(next);
    if (digits && idx < OTP_LEN - 1) otpRefs.current[idx + 1]?.focus();
    if (next.every((d) => d !== '')) submitOtp(next.join(''));
  };

  const onOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) otpRefs.current[idx - 1]?.focus();
  };

  return (
    <div className="min-h-screen w-full flex bg-slate-50">
      {/* Brand panel (desktop) */}
      <div className="cp-dark hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col justify-between p-12 text-white"
           style={{ background: 'linear-gradient(135deg,#0F1A13 0%,#12241a 55%,#173a29 100%)' }}>
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full opacity-[0.08]" style={{ background: '#A9FF53' }} />
        <div className="relative flex items-center gap-2 text-xl font-bold tracking-tight text-white">
          Derwent <span style={{ color: '#A9FF53' }}>Finance</span>
        </div>
        <div className="relative">
          <h2 className="text-4xl font-bold leading-tight text-white">One login.<br />Your whole home-loan journey.</h2>
          <p className="text-white/70 mt-4 max-w-sm text-[15px] leading-relaxed">
            Track your application in real time, upload documents securely and stay close to your broker — every step of the way.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-white/85">
            {['See exactly where your loan is up to', 'Upload documents in seconds', 'Message your broker any time'].map((t) => (
              <li key={t} className="flex items-center gap-3">
                <span className="w-5 h-5 rounded-full flex items-center justify-center flex-none" style={{ background: '#A9FF53' }}>
                  <Check className="w-3 h-3 text-[#0F1A13]" strokeWidth={3} />
                </span>
                {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative text-xs text-white/40">Bank-grade security · Two-factor protected · Your data stays private</div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-50 ring-1 ring-emerald-200 mb-4">
              {step === 'otp' ? <Smartphone className="w-7 h-7 text-emerald-600" /> : <ShieldCheck className="w-7 h-7 text-emerald-600" />}
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">
              Derwent <span className="text-emerald-600">Finance</span>
            </h1>
            <p className="text-slate-500 mt-1.5 text-sm">Your private home-loan portal</p>
          </div>

          <div className="bg-white ring-1 ring-slate-200 rounded-3xl shadow-xl shadow-slate-200/60 p-7">
            {step === 'password' && (
              <>
                <h2 className="text-xl font-semibold text-slate-900">Welcome back</h2>
                <p className="text-slate-500 text-sm mt-1">Sign in to track your loan, upload documents and chat with your broker.</p>

                <form onSubmit={submitPassword} className="mt-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="email"
                        required
                        autoFocus
                        data-testid="client-login-email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-50 ring-1 ring-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:bg-white transition"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type={showPw ? 'text' : 'password'}
                        required
                        data-testid="client-login-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full pl-9 pr-9 py-2.5 rounded-xl bg-slate-50 ring-1 ring-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:bg-white transition"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        aria-label={showPw ? 'Hide password' : 'Show password'}
                      >
                        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div data-testid="client-login-error" className="rounded-xl bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-sm px-3 py-2">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="client-login-submit"
                    style={{ color: '#ffffff' }}
                    className="w-full py-2.5 rounded-xl bg-[#0F1A13] hover:bg-[#173a29] font-semibold disabled:opacity-60 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Continue
                  </button>
                </form>

                <p className="text-xs text-slate-400 mt-6 text-center">
                  Need access? Ask your broker — they can send you an invite.
                </p>
              </>
            )}

            {step === 'otp' && (
              <>
                <button
                  type="button"
                  onClick={() => { setStep('password'); setError(''); setInfo(''); }}
                  data-testid="client-otp-back"
                  className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition mb-3"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <h2 className="text-xl font-semibold text-slate-900">Verify it's you</h2>
                <p className="text-slate-500 text-sm mt-1" data-testid="client-otp-hint">
                  Enter the 6-digit code we sent to{' '}
                  <span className="font-medium text-slate-700">{phoneHint || 'your mobile'}</span>.
                </p>

                <div className="mt-6 flex items-center justify-between gap-2" data-testid="client-otp-inputs">
                  {otp.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => (otpRefs.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={OTP_LEN}
                      data-testid={`client-otp-input-${i}`}
                      value={d}
                      onChange={(e) => onOtpChange(i, e.target.value)}
                      onKeyDown={(e) => onOtpKeyDown(i, e)}
                      className="w-11 h-14 text-center text-xl font-semibold rounded-xl bg-slate-50 ring-1 ring-slate-200 text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:bg-white transition"
                    />
                  ))}
                </div>

                {info && !error && (
                  <div data-testid="client-otp-info" className="mt-4 rounded-xl bg-emerald-50 ring-1 ring-emerald-200 text-emerald-800 text-sm px-3 py-2">
                    {info}
                  </div>
                )}
                {error && (
                  <div data-testid="client-otp-error" className="mt-4 rounded-xl bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-sm px-3 py-2">
                    {error}
                  </div>
                )}

                <button
                  type="button"
                  disabled={loading || otp.some((d) => d === '')}
                  data-testid="client-otp-verify"
                  onClick={() => submitOtp(otp.join(''))}
                  style={{ color: '#ffffff' }}
                  className="mt-5 w-full py-2.5 rounded-xl bg-[#0F1A13] hover:bg-[#173a29] font-semibold disabled:opacity-60 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Verify &amp; sign in
                </button>

                <div className="mt-4 text-center text-sm">
                  {resendIn > 0 ? (
                    <span className="text-slate-400">Resend code in {resendIn}s</span>
                  ) : (
                    <button type="button" onClick={resend} data-testid="client-otp-resend" className="text-emerald-700 font-medium hover:underline">
                      Resend code
                    </button>
                  )}
                </div>

                <p className="text-xs text-slate-400 mt-6 text-center">
                  Didn't get a code? Your broker can verify your identity and check your mobile number.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
